import { useEffect, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { api } from "@/lib/api";
import { useChat } from "@/hooks/use-chat";
import { ChatPanel } from "@/components/ChatPanel";
import { ArrowIcon, CheckIcon } from "@/components/icons";

const Step = ({ n, state, title, sub, to }) => {
  const dot =
    state === "done" ? (
      <div className="w-8 h-8 shrink-0 rounded-full bg-success text-white flex items-center justify-center">
        <CheckIcon size={16} />
      </div>
    ) : state === "current" ? (
      <div className="w-8 h-8 shrink-0 rounded-full bg-accent border border-foreground flex items-center justify-center text-sm font-semibold">
        {n}
      </div>
    ) : (
      <div className="w-8 h-8 shrink-0 rounded-full border border-[#B9B2A0] flex items-center justify-center text-sm font-semibold text-muted-foreground">
        {n}
      </div>
    );
  const body = (
    <>
      {dot}
      <div>
        <div className="text-base font-semibold">{title}</div>
        <div className="text-sm text-muted-foreground mt-0.5">{sub}</div>
      </div>
    </>
  );
  return to ? (
    <Link to={to} className="flex gap-3.5 items-start">{body}</Link>
  ) : (
    <div className="flex gap-3.5 items-start">{body}</div>
  );
};

export function Overview() {
  const { documents, billing } = useOutletContext();
  const chat = useChat();
  const [widget, setWidget] = useState(null);

  useEffect(() => {
    api.getWidgetStatus().then(setWidget).catch(() => {});
  }, []);

  const count = documents.length;
  const tested = chat.messages.some((m) => m.role === "user");
  const installed = !!widget?.installed;
  const done = [count > 0, tested, installed];
  const doneCount = done.filter(Boolean).length;
  const firstOpen = done.indexOf(false);
  const stateOf = (i) => (done[i] ? "done" : i === firstOpen ? "current" : "todo");

  const isPro = billing?.plan === "pro";
  const usage = billing?.usage;
  const used = usage?.used ?? 0;
  const limit = usage?.limit ?? 0;
  const pct = usage && !usage.unlimited && limit ? Math.min(100, Math.round((used / limit) * 100)) : 0;
  const left = Math.max(0, limit - used);

  const nextHint =
    firstOpen === -1
      ? "You are all set."
      : firstOpen === 0
        ? "Next: add a source, then test your assistant."
        : firstOpen === 1
          ? "Next: test your assistant, then install it."
          : "Next: install it on your site.";

  return (
    <div className="flex flex-col gap-5 lg:gap-7 lg:h-[calc(100vh-5rem)] lg:min-h-[720px]">
      <div className="flex justify-between items-end gap-4">
        <div className="flex flex-col gap-1.5">
          <h1 className="font-heading text-[40px] lg:text-5xl leading-none tracking-[-0.02em]">Overview</h1>
          <p className="hidden lg:block text-base text-soft">
            {count > 0
              ? `Your assistant is trained on ${count} document${count === 1 ? "" : "s"}. Test it, then put it on your site.`
              : "Add your first source to train your assistant."}
          </p>
        </div>
        <Link
          to="/dashboard/install"
          data-testid="overview-install-card"
          className="paper-dark hidden lg:flex h-[46px] px-[22px] items-center gap-2.5 rounded-xl text-primary-foreground text-[15px] font-medium"
        >
          Install on your site <ArrowIcon size={16} />
        </Link>
      </div>

      <div className="grid gap-5 lg:gap-8 lg:grid-cols-[minmax(0,1fr)_320px] lg:flex-1 lg:min-h-0">
        <div className="flex flex-col gap-5 lg:gap-6 lg:min-h-0">
          {/* Setup */}
          <div data-testid="setup-card" className="paper rounded-2xl border border-border p-4 lg:p-6">
            <div className="lg:hidden flex flex-col gap-3">
              <div className="flex justify-between items-baseline">
                <span className="text-[15px] font-semibold">Setup</span>
                <span className="font-mono text-xs text-muted-foreground">{doneCount} of 3 done</span>
              </div>
              <div className="flex gap-1.5">
                {done.map((d, i) => (
                  <div
                    key={i}
                    className={`flex-1 h-1.5 rounded-sm box-border ${
                      d ? "bg-success" : i === firstOpen ? "bg-accent border border-foreground" : "bg-track"
                    }`}
                  />
                ))}
              </div>
              <div className="text-sm text-soft">{nextHint}</div>
            </div>
            <div className="hidden lg:grid grid-cols-3 gap-6">
              <Step n={1} state={stateOf(0)} title="Add sources" sub={count ? `${count} document${count === 1 ? "" : "s"} added` : "Upload your docs"} to="/dashboard/sources" />
              <Step n={2} state={stateOf(1)} title="Test your assistant" sub={tested ? "Tested" : "Ask a question below"} />
              <Step n={3} state={stateOf(2)} title="Install on your site" sub={installed ? "Live" : "One script tag"} to="/dashboard/install" />
            </div>
          </div>

          {/* Usage (mobile only, compact) */}
          <div className="lg:hidden paper rounded-[14px] border border-border p-4 flex items-center justify-between">
            <div className="flex flex-col gap-1.5">
              <span className="text-sm text-muted-foreground">Messages this month</span>
              <span className="font-heading text-4xl leading-none">
                {used}
                <span className="font-sans text-[15px] text-muted-foreground">{isPro ? " · unlimited" : ` of ${limit}`}</span>
              </span>
            </div>
            <div className="w-[120px] h-2 rounded bg-track overflow-hidden">
              <div className="h-full bg-primary" style={{ width: `${isPro ? 8 : pct}%` }} />
            </div>
          </div>

          {/* Test chat */}
          <div
            data-testid="inline-chat"
            className="paper rounded-2xl border border-border flex flex-col overflow-hidden h-[460px] lg:h-auto lg:flex-1 lg:min-h-0"
          >
            <div className="h-12 lg:h-[60px] shrink-0 px-4 lg:px-6 flex items-center justify-between border-b border-border">
              <div className="flex items-center gap-2 lg:gap-2.5">
                <div className="w-2 h-2 lg:w-2.5 lg:h-2.5 rounded-full bg-success" />
                <span className="text-[15px] lg:text-base font-semibold">Test chat</span>
              </div>
              <div className="flex items-center gap-4">
                <span className="hidden lg:inline font-mono text-xs text-muted-foreground">
                  Answers use only your {count} document{count === 1 ? "" : "s"}
                </span>
                {chat.messages.length > 0 && (
                  <button
                    data-testid="clear-chat-button"
                    onClick={chat.clear}
                    className="text-xs text-muted-foreground underline hover:text-foreground"
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>
            <ChatPanel chat={chat} hasDocuments={count > 0} className="flex-1" />
          </div>
        </div>

        {/* Right column */}
        <div className="hidden lg:flex flex-col gap-6">
          <div data-testid="usage-card" className="paper rounded-2xl border border-border p-6 flex flex-col gap-4">
            <div className="flex justify-between items-center">
              <span className="text-base font-semibold">Messages this month</span>
              <span className="font-mono text-xs px-2.5 py-1 rounded-xl border border-input">{isPro ? "Pro" : "Free"}</span>
            </div>
            <div data-testid="usage-count" className="font-heading text-[56px] leading-none tracking-[-0.02em]">
              {used}
              <span className="font-sans text-lg tracking-normal text-muted-foreground">
                {isPro ? " · unlimited" : ` of ${limit}`}
              </span>
            </div>
            <div className="h-2.5 rounded-md bg-track overflow-hidden">
              <div className="h-full bg-primary" style={{ width: `${isPro ? 8 : pct}%` }} />
            </div>
            <div className="text-sm text-soft">
              {isPro ? "Unlimited on Pro." : `${left} message${left === 1 ? "" : "s"} left. Resets on the 1st.`}
            </div>
          </div>

          {!isPro && billing && (
            <div className="paper-dark rounded-2xl text-primary-foreground p-6 flex flex-col gap-3.5">
              <div className="text-lg font-semibold">Never hit a limit</div>
              <div className="text-[15px] leading-normal text-[#D8D4C8]">
                Pro gives unlimited messages and higher widget traffic.
              </div>
              <div className="font-heading text-[40px] leading-none">
                $29<span className="font-sans text-[15px] text-[#D8D4C8]"> /month</span>
              </div>
              <Link
                to="/dashboard/billing"
                data-testid="upgrade-button"
                className="h-[46px] flex items-center justify-center rounded-xl bg-accent text-accent-foreground text-[15px] font-semibold"
              >
                Upgrade to Pro
              </Link>
            </div>
          )}

          <div className="paper rounded-2xl border border-border p-6 flex flex-col gap-3.5">
            <div className="flex justify-between items-center">
              <span className="text-base font-semibold">Widget</span>
              <span
                className={`flex items-center gap-2 text-[13px] px-2.5 py-1 rounded-xl ${
                  installed ? "text-success bg-success/10" : "text-[#8A4B00] bg-[#FFF1D6]"
                }`}
              >
                <span className={`w-2 h-2 rounded-full ${installed ? "bg-success" : "bg-[#B45309]"}`} />
                {installed ? "Live" : "Not installed"}
              </span>
            </div>
            <div className="text-sm leading-normal text-soft">
              {installed
                ? `Visitors have sent ${widget.messages} message${widget.messages === 1 ? "" : "s"}.`
                : "Visitors cannot reach your assistant yet."}
            </div>
            <Link to="/dashboard/install" className="text-sm font-semibold underline">
              {installed ? "View your script tag" : "Get your script tag"}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
